/// <reference path="../../globals.d.ts" />
import path from 'node:path';
import fsSync from 'node:fs';
import fs from 'node:fs/promises';
import { createRequire } from 'node:module';
import { pathToFileURL } from 'node:url';
import crypto from 'node:crypto';
import https from 'node:https';
import ansis from 'ansis';
import semverValid from 'semver/functions/valid';
import semverValidRange from 'semver/ranges/valid';
import semverSatisfies from 'semver/functions/satisfies';
import { isCancel } from '@clack/prompts';
import _ from '@env-spec/utils/my-dash';
import { pathExists } from '@env-spec/utils/fs-utils';
import { getUserVarlockDir } from '../../lib/user-config-dir';
import { PluginCacheAccessor } from '../../lib/cache/plugin-cache-accessor';
import { NoopCacheStore } from '../../lib/cache/noop-cache-store';
import type { CacheStoreLike } from '../../lib/cache/cache-store';
import { parseTtl } from '../../lib/cache/ttl-parser';
import { resolveCacheTtl } from '../../lib/cache/resolve-cache-ttl';
import { confirm } from '../../cli/helpers/prompts';
import { extractTarball } from '../../lib/extract-tarball';


import { FileBasedDataSource, type EnvGraphDataSource } from './data-source';
import {
  CoercionError, LoadingError, ResolutionError, SchemaError, ValidationError, VarlockError,
} from './errors';
import { getErrorLocation } from './error-location';
import { createResolver, type ResolverDef } from './resolver';
import type {
  DecoratorInstance, ItemDecoratorDef, RootDecoratorDef, RootDecoratorInstance,
} from './decorators';
import { createEnvGraphDataType } from './data-types';
import type { CodeGeneratorDef } from './type-generation';

import { createDebug, type Debugger } from '../../lib/debug';
import { getWorkspaceInfo } from '../../lib/workspace-utils';
import { activatePlugin, deactivatePlugin, pluginProxy } from '../../plugin-context';
import type { EnvGraph } from './env-graph';

// module caching means the file will not be executed multiple times
// so we track just to ensure we don't attempt to do load it multiple times
const importedPluginModulePaths = new Set<string>();

// One-time Bun compat patch applied before any plugin loads.
// In Bun, globalThis.crypto IS the Web Crypto API (no .webcrypto sub-property).
// CJS bundles (e.g. bitwarden) use `crypto.webcrypto.subtle`, so we patch it once.
let _cryptoShimApplied = false;
function applyBunCryptoShim() {
  if (_cryptoShimApplied) return;
  _cryptoShimApplied = true;
  const globalCrypto = (globalThis as any).crypto;
  if (globalCrypto && !globalCrypto.webcrypto) {
    try {
      Object.defineProperty(globalCrypto, 'webcrypto', {
        get() { return globalCrypto; },
        configurable: true,
        enumerable: false,
      });
    } catch {
      // ignore if crypto object is not extensible
    }
  }
}

const isBunRuntime = !!process.versions.bun || __VARLOCK_SEA_BUILD__;

// In SEA (compiled binary) builds, `varlock` does not exist in the user's
// node_modules so `import { plugin } from 'varlock/plugin-lib'` inside an
// external plugin file would fail with MODULE_NOT_FOUND.
//
// We handle this by transforming the plugin source at load time — replacing
// the `varlock/plugin-lib` import with a globalThis accessor. Single-file
// plugins are restricted from having other imports, so this is the only
// substitution needed.
// In SEA builds, varlock isn't in node_modules so we must always provide these.
// In non-SEA CJS, we still intercept require('varlock/plugin-lib') to guarantee
// the same module instance (dist/plugin-lib.js would be a separate copy from the
// source modules loaded by vitest/ts-node, causing the plugin proxy to diverge).
const varlockPluginLibExports = {
  plugin: pluginProxy,
  ValidationError,
  CoercionError,
  SchemaError,
  ResolutionError,
  createDebug,
  parseTtl,
  resolveCacheTtl,
};


/**
 * Loads and executes a CJS plugin module.
 *
 * Plugins are built as CJS. We use `new Function` to create a CJS module scope
 * (exports, require, module, __filename, __dirname) — this runs in the main
 * Node.js/Bun context so all built-in globals (DOMException, fetch, etc.) work
 * correctly. `require('varlock/plugin-lib')` is intercepted to return the
 * plugin context, so plugin code accesses it via a standard require call.
 *
 * We intentionally avoid vm.createContext with a Proxy sandbox because Node.js
 * C++ lazy property initializers (e.g. for DOMException) assert IsolateData
 * exists on the context, which is not set up for Proxy-based vm contexts.
 */
function loadPluginModuleCJS(filePath: string): void {
  applyBunCryptoShim();

  const code = fsSync.readFileSync(filePath, 'utf-8');
  const pluginDir = path.dirname(filePath);
  const moduleObj = { exports: {} as any };
  const baseRequire = createRequire(filePath);
  const requireFn = (id: string) => {
    if (id === 'varlock/plugin-lib') return varlockPluginLibExports;
    return baseRequire(id);
  };
  requireFn.resolve = baseRequire.resolve.bind(baseRequire);
  requireFn.main = baseRequire.main;
  requireFn.cache = baseRequire.cache;

  // eslint-disable-next-line no-new-func
  const moduleFn = new Function('exports', 'require', 'module', '__filename', '__dirname', code);
  moduleFn(moduleObj.exports, requireFn, moduleObj, filePath, pluginDir);
}

/**
 * Loads and executes an ESM or TypeScript plugin module via dynamic import().
 *
 * In SEA builds, `import { plugin } from 'varlock/plugin-lib'` is replaced
 * at load time with a globalThis accessor (varlock isn't in the user's
 * node_modules). Single-file plugins are restricted from other imports so
 * this substitution is sufficient. In non-SEA environments varlock resolves
 * naturally via node_modules.
 *
 * TypeScript (.ts) files are supported natively under Bun's runtime.
 *
 * A cache-busting query param (non-SEA) or unique temp filename (SEA) ensures
 * the module re-executes on each load, which matters for test fixtures.
 */
async function loadPluginModuleESM(filePath: string): Promise<void> {
  if (__VARLOCK_SEA_BUILD__) {
    (globalThis as any).__varlockPluginLib = varlockPluginLibExports;

    let source = (await fs.readFile(filePath, 'utf-8')).replace(
      /import\s*\{([^}]+)\}\s*from\s*['"]varlock\/plugin-lib['"]/g,
      (_match: string, imports: string) => `const {${imports}} = globalThis.__varlockPluginLib;`,
    );

    if (path.extname(filePath) === '.ts') {
      // @ts-ignore - Bun global only available in Bun runtime
      source = new Bun.Transpiler({ loader: 'ts' }).transformSync(source);
    }

    await import(/* webpackIgnore: true */ /* @vite-ignore */ `data:text/javascript,${encodeURIComponent(source)}`);
  } else {
    const fileUrl = pathToFileURL(filePath).href;
    await import(/* webpackIgnore: true */ /* @vite-ignore */ `${fileUrl}?t=${Date.now()}`);
  }
}


/** Allowed value types for plugin telemetry attributes (strictly sanitized before send) */
export type PluginTelemetryAttributeValue = boolean | number | string | null;
/** Flat object of anonymous, non-sensitive usage attributes a plugin reports for telemetry */
export type PluginTelemetryAttributes = Record<string, PluginTelemetryAttributeValue>;

export class VarlockPlugin {
  // helper so end user code can get same error classes
  readonly ERRORS = {
    ValidationError,
    CoercionError,
    SchemaError,
    ResolutionError,
  };

  private _packageJson?: Record<string, any>;

  private _name?: string;
  get name() { return this._packageJson?.name || this._name || 'unnamed plugin'; }
  set name(val: string) { this._name = val; }

  private _version?: string;
  get version() { return this._packageJson?.version || this._version || '0.0.0'; }
  set version(val: string) { this._version = val; }

  private _icon?: string;
  get icon() { return this._icon || 'mdi:puzzle'; }
  set icon(val: string) { this._icon = val; }

  loadingError?: VarlockError;
  warnings: Array<SchemaError> = [];

  readonly localPath?: string;

  /** reference to the `@plugin()` decorator instance(s) that installed the plugin  */
  installDecoratorInstances: Array<DecoratorInstance> = [];

  type: 'single-file' | 'package';

  constructor(opts: {
    type: 'single-file' | 'package',
    localPath: string,
    loadingError?: VarlockError,
    packageJson?: { name: string; version?: string; description?: string };
  }) {
    this.type = opts.type;
    this.localPath = opts?.localPath;
    this._packageJson = opts?.packageJson;
  }

  // awkwardly using get here to make sure we bind the debug function to this
  // which lets us destructure it in plugin code
  private debugger: Debugger | undefined;
  get debug() {
    return (...args: Parameters<Debugger>) => {
      if (!this.debugger) {
        if (!this.name) throw new Error('expected plugin name to be set before using debug');
        this.debugger = createDebug(`varlock:plugin:${this.name}`);
      }
      return this.debugger(...args);
    };
  }


  // -- Cache API for plugin authors --
  private _cacheAccessor?: PluginCacheAccessor;
  /** @internal set by EnvGraph when plugins are loaded */
  _cacheStore?: CacheStoreLike;

  /**
   * Scoped cache accessor for this plugin.
   * Keys are automatically namespaced to prevent collisions between plugins.
   */
  get cache(): PluginCacheAccessor {
    // when caching is unavailable (--skip-cache / @cache=disabled), hand out a
    // no-op-backed accessor so plugin code doesn't need to special-case it
    this._cacheAccessor ||= new PluginCacheAccessor(this.name, this._cacheStore ?? new NoopCacheStore());
    return this._cacheAccessor;
  }

  readonly dataTypes?: Array<Parameters<typeof createEnvGraphDataType>[0]> = [];
  registerDataType(dataTypeDef: Parameters<typeof createEnvGraphDataType>[0]) {
    this.debug('registerDataType', dataTypeDef.name);
    this.dataTypes!.push(dataTypeDef);
  }

  readonly rootDecorators?: Array<RootDecoratorDef<any>> = [];
  registerRootDecorator<T>(decoratorDef: RootDecoratorDef<T>) {
    this.debug('registerRootDecorator', decoratorDef.name);
    this.rootDecorators!.push(decoratorDef);
  }

  readonly codeGenerators?: Array<CodeGeneratorDef> = [];
  /**
   * Register a code generator contributed by this plugin. Each generator is triggered by a root
   * decorator (named `decoratorName`) and produces a file — the same mechanism the built-in
   * ts/py/rs/go/php generators use.
   */
  registerCodeGenerator(generatorDef: CodeGeneratorDef) {
    this.debug('registerCodeGenerator', generatorDef.decoratorName);
    this.codeGenerators!.push(generatorDef);
  }

  readonly itemDecorators?: Array<ItemDecoratorDef<any>> = [];
  registerItemDecorator<T>(decoratorDef: ItemDecoratorDef<T>) {
    this.debug('registerItemDecorator', decoratorDef.name);
    this.itemDecorators!.push(decoratorDef);
  }

  readonly resolverFunctions?: Array<ResolverDef<any>> = [];
  registerResolverFunction<T>(resolverDef: ResolverDef<T>) {
    this.debug('registerResolverFunction', resolverDef.name);
    this.resolverFunctions!.push(resolverDef);
  }

  /** @internal telemetry attributes provider registered by the plugin (collected for official plugins only) */
  _getTelemetryAttributes?: () => PluginTelemetryAttributes;
  /**
   * Register a function returning a flat object of anonymous, non-sensitive usage
   * attributes for this plugin (booleans, short enum strings, counts) — e.g. which
   * auth mode is in use, whether a feature is enabled. Called when telemetry is
   * captured. Values are strictly sanitized and only collected for official
   * `@varlock/*` plugins. Throwing or returning unexpected shapes is safe —
   * offending entries are dropped. Never include secret values, names, or paths.
   */
  registerTelemetryAttributes(fn: () => PluginTelemetryAttributes) {
    this.debug('registerTelemetryAttributes');
    this._getTelemetryAttributes = fn;
  }

  /**
   * Declare standard env vars this plugin uses.
   * Set during plugin init — the loading infrastructure will automatically
   * check for these vars and generate warnings if they are detected but not wired up.
   *
   * `key` accepts a single env var name or an array of alternatives — the first match is used.
   * `dataType` is used to generate `# @type=...` schema lines for vars not in the schema.
   */
  standardVars?: {
    initDecorator: string,
    params: Record<string, { key: string | Array<string>, dataType?: string }>,
  };

  /** called by the loading infrastructure — checks declared standardVars against the graph */
  _checkStandardVars(graph: {
    overrideValues: Record<string, string | undefined>,
    configSchema: Record<string, any>,
    sortedDataSources: Iterable<{
      rootDecorators: Array<{
        name: string, isFunctionCall: boolean, decValueResolver?: { deps: Array<string> },
      }>,
    }>,
  }) {
    if (!this.standardVars) return;
    const { initDecorator, params } = this.standardVars;

    // resolve each param to the first matching env key
    const resolved = Object.entries(params).map(([paramName, { key, dataType }]) => {
      const keys = Array.isArray(key) ? key : [key];
      const matchedKey = keys.find((k) => graph.overrideValues[k]);
      return {
        paramName, matchedKey, resolvedKey: matchedKey || keys[0], dataType,
      };
    });

    // collect config item keys already wired via init decorator instances
    const initDecName = initDecorator.replace(/^@/, '');
    const wiredVarNames = new Set<string>();
    for (const source of graph.sortedDataSources) {
      for (const rootDec of source.rootDecorators) {
        if (rootDec.name === initDecName && rootDec.isFunctionCall && rootDec.decValueResolver) {
          for (const dep of rootDec.decValueResolver.deps) wiredVarNames.add(dep);
        }
      }
    }

    // filter: only warn about vars detected in environment but NOT wired to the init decorator
    const detected = resolved.filter((v) => v.matchedKey && !wiredVarNames.has(v.matchedKey));
    if (detected.length === 0) return;

    const detectedKeys = detected.map((v) => v.matchedKey!);
    const needsSchemaSet = new Set(
      detected.filter((v) => !(v.resolvedKey in graph.configSchema)).map((v) => v.resolvedKey),
    );

    const wiringParams = detected
      .map((v) => ansis.green(`${v.paramName}=$${v.resolvedKey}`))
      .join(', ');

    const tip: Array<string> = [
      '',
      'Include in your schema and use in plugin initialization:',
      '',
      `  # ${initDecorator}(..., ${wiringParams})`,
      '  # ---',
    ];

    for (const v of detected) {
      const inSchema = !needsSchemaSet.has(v.resolvedKey);
      if (v.dataType) {
        const typeLine = `  # @type=${v.dataType}`;
        tip.push(inSchema ? typeLine : ansis.green(typeLine));
      }
      const itemLine = `  ${v.resolvedKey}=`;
      tip.push(inSchema ? itemLine : ansis.green(itemLine));
    }

    tip.push('');

    this.warnings.push(new SchemaError(
      `${detectedKeys.join(', ')} found in environment but not connected to plugin`,
      { isWarning: true, tip },
    ));
  }

  get pluginFilePath() {
    if (this.type === 'single-file') return this.localPath!;
    const pluginExport = this._packageJson?.exports?.['./plugin'] || '';
    if (!pluginExport) throw new Error('Plugin package.json is missing ./plugin export');
    return path.join(this.localPath!, pluginExport);
  }

  async executePluginModule() {
    activatePlugin(this);

    // Install a trap on globalThis.plugin so that old plugins which relied on
    // the implicit `plugin` global get a clear migration error instead of a
    // confusing "Cannot set properties of undefined" TypeError.
    const hadGlobalPlugin = 'plugin' in globalThis;
    const prevGlobalPlugin = (globalThis as any).plugin;
    const pluginGlobalRemovedMsg = `[varlock] Plugin "${this.name}" is incompatible with this version of varlock.`
      + ' Please upgrade the plugin to the latest version.';
    Object.defineProperty(globalThis, 'plugin', {
      get() { throw new Error(pluginGlobalRemovedMsg); },
      set() { throw new Error(pluginGlobalRemovedMsg); },
      configurable: true,
    });

    try {
      // slightly nicer error than the default MODULE_NOT_FOUND
      if (!await pathExists(this.pluginFilePath)) throw new Error(`Plugin file not found: ${this.pluginFilePath}`);

      importedPluginModulePaths.add(this.pluginFilePath);

      const ext = path.extname(this.pluginFilePath).toLowerCase();
      if (ext === '.mjs' || ext === '.ts') {
        await loadPluginModuleESM(this.pluginFilePath);
      } else {
        loadPluginModuleCJS(this.pluginFilePath);
      }
    } catch (err) {
      this.loadingError = err instanceof VarlockError ? err : new LoadingError(err as Error);
    } finally {
      // Restore globalThis.plugin to its previous state
      if (hadGlobalPlugin) {
        Object.defineProperty(globalThis, 'plugin', {
          value: prevGlobalPlugin,
          writable: true,
          configurable: true,
          enumerable: true,
        });
      } else {
        delete (globalThis as any).plugin;
      }
      deactivatePlugin();
    }
  }
}



async function initPluginFromLocalPath(localPath: string) {
  const stats = await fs.stat(localPath);

  // If it's a file, load the plugin directly
  if (stats.isFile()) {
    const ext = path.extname(localPath).toLowerCase();
    if (ext === '.ts' && !isBunRuntime) {
      throw new SchemaError(`TypeScript plugins (.ts) require Bun — try renaming to .mjs or compiling to .js first: ${localPath}`);
    }
    if (!['.js', '.cjs', '.mjs', '.ts'].includes(ext)) {
      throw new SchemaError(`Single-file plugin must be a .js, .cjs, .mjs, or .ts file: ${localPath}`);
    }

    return new VarlockPlugin({
      type: 'single-file',
      localPath,
    });

  // If it's a directory, load package.json and use exports field
  } else if (stats.isDirectory()) {
    const pkgJsonPath = path.join(localPath, 'package.json');
    if (!(await pathExists(pkgJsonPath))) {
      throw new SchemaError('Plugin is missing package.json file');
    }

    const packageJsonContents = JSON.parse(await fs.readFile(pkgJsonPath, 'utf-8'));
    if (!packageJsonContents.exports?.['./plugin']) {
      throw new SchemaError('Plugin is missing "./plugin" export in package.json');
    }

    return new VarlockPlugin({
      type: 'package',
      localPath,
      packageJson: packageJsonContents,
    });
  } else {
    throw new Error(`Invalid plugin path (not a file or directory): ${localPath}`);
  }
}


async function registerPluginInGraph(graph: EnvGraph, plugin: VarlockPlugin, pluginDecorator: RootDecoratorInstance) {
  let existingPlugin: VarlockPlugin | undefined;
  for (const possibleMatchingPlugin of graph.plugins) {
    if (plugin.type === 'single-file') {
      if (possibleMatchingPlugin.type === 'single-file' && possibleMatchingPlugin.localPath === plugin.localPath) {
        existingPlugin = possibleMatchingPlugin;
      }
    } else if (plugin.type === 'package') {
      if (possibleMatchingPlugin.name === plugin.name) {
        if (possibleMatchingPlugin.version === plugin.version) {
          const installedInSources = possibleMatchingPlugin.installDecoratorInstances.map((dec) => dec.dataSource);
          if (installedInSources.includes(pluginDecorator.dataSource)) {
            pluginDecorator._errors.push(new SchemaError(`Plugin ${plugin.name} already installed in this data source`));
            return;
          }

          existingPlugin = possibleMatchingPlugin;
        } else {
          pluginDecorator._errors.push(new SchemaError(`Plugin ${plugin.name} version conflict: tried to install version ${plugin.version} but version ${possibleMatchingPlugin.version} is already installed`));
          return;
        }
      }
    }
  }
  if (existingPlugin) {
    existingPlugin.installDecoratorInstances.push(pluginDecorator);
    return;
  }

  plugin.installDecoratorInstances.push(pluginDecorator);
  graph.plugins.push(plugin);

  // propagate cache store so plugin.cache is available during module execution
  if (graph._cacheStore) {
    plugin._cacheStore = graph._cacheStore;
  }

  // this finally executes the plugin code
  await plugin.executePluginModule();

  // if plugin failed to load, don't try to register its exports
  if (plugin.loadingError) {
    return;
  }

  // register decorators, resolvers, data types from this plugin
  for (const rootDec of plugin.rootDecorators || []) {
    graph.registerRootDecorator(rootDec);
  }
  for (const codeGen of plugin.codeGenerators || []) {
    graph.registerCodeGenerator(codeGen);
  }
  for (const itemDec of plugin.itemDecorators || []) {
    graph.registerItemDecorator(itemDec);
  }
  for (const dataType of plugin.dataTypes || []) {
    graph.registerDataType(createEnvGraphDataType(dataType));
  }
  for (const resolverDef of plugin.resolverFunctions || []) {
    // might want to move into plugin load process
    graph.registerResolver(createResolver(resolverDef));
  }
}

async function isPluginCached(url: string): Promise<boolean> {
  const cacheDir = path.join(getUserVarlockDir(), 'plugins-cache');
  const indexPath = path.join(cacheDir, 'index.json');
  try {
    const indexRaw = await fs.readFile(indexPath, 'utf-8');
    const index = JSON.parse(indexRaw) as Record<string, string>;
    if (index[url]) {
      const pluginDir = path.join(cacheDir, index[url]);
      return fs.stat(pluginDir).then(() => true, () => false);
    }
  } catch {
    // ignore
  }
  return false;
}

async function downloadPlugin(url: string) {
  const cacheDir = path.join(getUserVarlockDir(), 'plugins-cache');
  const indexPath = path.join(cacheDir, 'index.json');
  await fs.mkdir(cacheDir, { recursive: true });

  // Load or create index.json
  let index: Record<string, string> = {};
  try {
    const indexRaw = await fs.readFile(indexPath, 'utf-8');
    index = JSON.parse(indexRaw);
  } catch {
    // ignore, treat as empty
  }

  if (index[url]) {
    const pluginDir = path.join(cacheDir, index[url]);
    if (await fs.stat(pluginDir).then(() => true, () => false)) {
      return pluginDir;
    }
    // If mapping exists but folder is missing, fall through to re-download
  }

  // Download the file
  const tmpTgz = path.join(cacheDir, `tmp-${crypto.randomBytes(8).toString('hex')}.tgz`);
  await new Promise<void>((resolve, reject) => {
    const file = fsSync.createWriteStream(tmpTgz);
    https.get(url, (res) => {
      if (res.statusCode !== 200) {
        reject(new Error(`Failed to download plugin: ${res.statusCode}`));
        return;
      }
      res.pipe(file);
      file.on('finish', () => file.close(() => resolve()));
      file.on('error', reject);
    }).on('error', reject);
  });

  // Extract tgz to a temp folder. We extract natively (zlib + a small tar
  // reader) rather than shelling out to `tar`, so plugin auto-install works in
  // minimal/distroless images that have neither a shell nor a `tar` binary.
  const tmpExtractDir = path.join(cacheDir, `tmp-extract-${crypto.randomBytes(8).toString('hex')}`);
  await fs.mkdir(tmpExtractDir);
  await extractTarball(tmpTgz, tmpExtractDir);

  // Find package.json (assume in package/ or root)
  let pkgJsonPath = path.join(tmpExtractDir, 'package', 'package.json');
  let pluginRoot = path.join(tmpExtractDir, 'package');
  if (!(await fs.stat(pkgJsonPath).then(() => true, () => false))) {
    pkgJsonPath = path.join(tmpExtractDir, 'package.json');
    pluginRoot = tmpExtractDir;
    if (!(await fs.stat(pkgJsonPath).then(() => true, () => false))) {
      throw new Error('package.json not found in plugin tgz');
    }
  }
  const pkgJson = JSON.parse(await fs.readFile(pkgJsonPath, 'utf-8'));

  // Generate unique hash for folder name
  const safePackageName = (pkgJson.name || '').replaceAll('/', '-').replaceAll('@', '');
  const dirName = `${safePackageName}_${(pkgJson.version || '')}_${crypto.randomBytes(4).toString('hex')}`;
  const finalDir = path.join(cacheDir, dirName);

  // Move extracted folder to finalDir
  await fs.rm(finalDir, { recursive: true, force: true });
  await fs.rename(pluginRoot, finalDir);
  await fs.rm(tmpTgz, { force: true });
  await fs.rm(tmpExtractDir, { recursive: true, force: true });

  // Update index.json file with mapping b/w url and new folder
  index[url] = dirName;
  await fs.writeFile(indexPath, JSON.stringify(index, null, 2));

  return finalDir;
}

/**
 * Fetches plugin metadata from npm and downloads the tarball into the local cache.
 * The caller is responsible for any user confirmation — this function downloads unconditionally.
 *
 * @param moduleName  e.g. `@varlock/1password-plugin` or `my-plugin`
 * @param versionDescriptor  must be a fixed semver version e.g. `1.2.3`
 * @returns the local cache directory the plugin was extracted into
 */
export async function downloadPluginToCache(moduleName: string, versionDescriptor: string): Promise<string> {
  if (!semverValid(versionDescriptor)) {
    throw new Error(`"${versionDescriptor}" is not a fixed version — use an exact version like 1.2.3`);
  }

  const npmInfoUrl = `https://registry.npmjs.org/${moduleName}/${versionDescriptor}`;
  const npmInfoReq = await fetch(npmInfoUrl);
  if (!npmInfoReq.ok) {
    throw new Error(`Failed to fetch plugin "${moduleName}@${versionDescriptor}" from npm: ${npmInfoReq.status} ${npmInfoReq.statusText}`);
  }
  const npmInfo = await npmInfoReq.json() as { dist?: { tarball?: string } };
  const tarballUrl = npmInfo?.dist?.tarball;
  if (!tarballUrl) {
    throw new Error(`Failed to find tarball URL for plugin "${moduleName}@${versionDescriptor}" from npm`);
  }

  return downloadPlugin(tarballUrl);
}


export async function processPluginInstallDecorators(dataSource: EnvGraphDataSource) {
  const graph = dataSource.graph;
  if (!graph) throw new Error('Data source not attached to graph');

  // handle plugin decorators
  const installPluginDecorators = dataSource.getRootDecFns('plugin');
  if (installPluginDecorators.length) {
    if (!(dataSource instanceof FileBasedDataSource)) {
      dataSource._errors.push(new SchemaError('@plugin can only be used from a file-based data source'));
      return;
    }
    const dataSourceDir = path.dirname(dataSource.fullPath);
    for (const pluginDecorator of installPluginDecorators) {
      let pluginSrcPath: string | undefined;
      try {
        const installPluginArgs = await pluginDecorator.resolve();
        const pluginSourceDescriptor = installPluginArgs.arr[0];
        if (!_.isString(pluginSourceDescriptor)) {
          throw new SchemaError('Bad @plugin - must provide a string source location');
        }
        // install from local file path
        if (pluginSourceDescriptor.startsWith('./') || pluginSourceDescriptor.startsWith('../') || pluginSourceDescriptor.startsWith('/')) {
          pluginSrcPath = pluginSourceDescriptor.startsWith('/') ? pluginSourceDescriptor : path.resolve(dataSourceDir, pluginSourceDescriptor);
          if (!(await pathExists(pluginSrcPath))) {
            // in this case, the bad path is the user's fault
            throw new SchemaError(`Bad @plugin path: ${pluginSourceDescriptor}`);
          }
        } else if (pluginSourceDescriptor.includes(':')) {
          const protocol = pluginSourceDescriptor.split(':')[0];
          // protocols that we will likely support in future
          if (['https', 'npm', 'jsr', 'git'].includes(protocol)) {
            throw new SchemaError(`@plugin source protocol "${protocol}" is not yet supported`);
          } else {
            throw new SchemaError(`Bad @plugin source protocol: ${protocol}`);
          }

        // we will assume its a npm module name - `packageName` / `packageName@version`
        } else {
          const atLocation = pluginSourceDescriptor.indexOf('@', 1);
          let versionDescriptor: string | undefined;
          let moduleName: string | undefined;
          if (atLocation === -1) {
            moduleName = pluginSourceDescriptor;
          } else {
            moduleName = pluginSourceDescriptor.slice(0, atLocation);
            versionDescriptor = pluginSourceDescriptor.slice(atLocation + 1);
          }

          const semverRange = semverValidRange(versionDescriptor);
          if (versionDescriptor && !semverRange) {
            throw new SchemaError(`Bad @plugin version descriptor: ${versionDescriptor}`);
          } else if (semverRange === '*') {
            throw new SchemaError(`Version descriptor "${versionDescriptor}" is too broad`);
          } else if (versionDescriptor === '') {
            throw new SchemaError('Bad @plugin version descriptor - remove "@" or specify a valid version');
          }

          // Walk up the directory tree checking each node_modules for the plugin.
          // This supports monorepos where npm/yarn/pnpm may hoist packages to the
          // root node_modules rather than the workspace's own node_modules.
          const workspaceRootPath = getWorkspaceInfo()?.rootPath;

          let currentDir = dataSourceDir;
          let nodeModulesPath: string | undefined;
          while (currentDir) {
            if (!nodeModulesPath && await pathExists(path.join(currentDir, 'package.json'))) {
              // Track the nearest package.json's node_modules for error messages
              nodeModulesPath = path.join(currentDir, 'node_modules');
            }

            const candidatePluginPath = path.join(currentDir, 'node_modules', moduleName);
            if (await pathExists(candidatePluginPath)) {
              // TODO: cache the package.json since we will read it again later
              const pluginPackageJsonPath = path.join(candidatePluginPath, 'package.json');
              const packageJsonString = await fs.readFile(pluginPackageJsonPath, 'utf-8');
              const packageJson = JSON.parse(packageJsonString);
              const packageVersion = packageJson.version;
              if (versionDescriptor && !semverSatisfies(packageVersion, versionDescriptor)) {
                throw new SchemaError(`Installed plugin "${moduleName}" version "${packageVersion}" does not satisfy requested version "${versionDescriptor}"`, {
                  location: getErrorLocation(dataSource, pluginDecorator),
                });
              }
              pluginSrcPath = candidatePluginPath;
              break;
            }

            // stop at the workspace root - no need to search beyond it
            if (workspaceRootPath && currentDir === workspaceRootPath) break;

            const parentDir = path.dirname(currentDir);
            if (parentDir === currentDir) break; // will stop when we reach the filesystem root
            currentDir = parentDir;
          }

          // attempt to fetch from npm if we did not succeed getting a local path above
          if (!pluginSrcPath) {
            if (!versionDescriptor) {
              // this tells us if the user is using package.json, so we can make the error message more helpful
              if (nodeModulesPath) {
                throw new SchemaError(`Plugin "${moduleName}" unable to resolve - install locally via your package.json file`);
              } else {
                throw new SchemaError(`Plugin "${moduleName}" unable to resolve - set a fixed version (e.g., \`@plugin(${moduleName}@1.2.3)\`)`);
              }
            } else if (!semverValid(versionDescriptor)) {
              throw new SchemaError(`Plugin "${moduleName}" must use a fixed version when not installing via package.json (e.g., \`@plugin(${moduleName}@1.2.3)\`)`, {
                location: getErrorLocation(dataSource, pluginDecorator),
              });
            }

            // ex: https://registry.npmjs.org/@varlock/plugin-name/1.2.3
            const npmInfoUrl = `https://registry.npmjs.org/${moduleName}/${versionDescriptor}`;
            const npmInfoReq = await fetch(npmInfoUrl);
            if (!npmInfoReq.ok) {
              // TODO: new error type? check for 404 vs others and give better message
              throw new Error(`Failed to fetch plugin "${moduleName}@${versionDescriptor}" from npm: ${npmInfoReq.status} ${npmInfoReq.statusText}`);
            }
            const npmInfo = await npmInfoReq.json() as { dist?: { tarball?: string } };
            const tarballUrl = npmInfo?.dist?.tarball;
            if (!tarballUrl) {
              throw new Error(`Failed to find tarball URL for plugin "${moduleName}@${versionDescriptor}" from npm`);
            }

            // Third-party plugins (non-@varlock) require user confirmation before downloading.
            // Official @varlock plugins are always trusted. If already cached (previously confirmed),
            // skip the prompt — the user has already blessed this specific version.
            if (!moduleName.startsWith('@varlock/') && !(await isPluginCached(tarballUrl))) {
              if (!process.stdout.isTTY || !process.stdin.isTTY) {
                throw new SchemaError(
                  `Third-party plugin "${moduleName}@${versionDescriptor}" must be confirmed before downloading, `
                  + 'but no interactive terminal (TTY) is available. '
                  + 'Run `varlock install-plugin` to pre-cache the plugin, or install it via your package.json.',
                );
              }

              process.stdout.write(
                `\n${ansis.yellow('⚠')}  Third-party plugin download requested\n`
                + `   Package: ${ansis.bold(`${moduleName}@${versionDescriptor}`)}\n`
                + '   Source:  npm registry (https://registry.npmjs.org)\n\n'
                + `   ${ansis.italic('Only install plugins from sources you trust.')}\n\n`,
              );

              const confirmed = await confirm({
                message: `Allow downloading "${moduleName}@${versionDescriptor}" from npm?`,
                active: 'Yes, download it',
                inactive: 'No, cancel',
                initialValue: false,
              });

              if (isCancel(confirmed) || !confirmed) {
                throw new SchemaError(`Third-party plugin "${moduleName}" download cancelled`);
              }
            }

            // downloads into local cache folder (user varlock config dir / plugins-cache/)
            const downloadedPluginPath = await downloadPlugin(tarballUrl);
            pluginSrcPath = downloadedPluginPath;
          }
        }

        const plugin = await initPluginFromLocalPath(pluginSrcPath);
        // might return an existing plugin if matches one in the graph
        await registerPluginInGraph(graph, plugin, pluginDecorator);
      } catch (err) {
        pluginDecorator._errors.push(err instanceof SchemaError ? err : new SchemaError(err as Error));
        continue;
      }
    }
  }
}

export type VarlockPluginCtx = {
  debug: Debugger,
  errors: {
    ValidationError: typeof ValidationError,
    CoercionError: typeof CoercionError,
    SchemaError: typeof SchemaError,
    ResolutionError: typeof ResolutionError,
  }
};

export type definePluginFn = (p: VarlockPlugin) => void;
